/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  ScrollView,
  Animated,
} from 'react-native';
import { PanGestureHandler } from 'react-native-gesture-handler';
import Colors from '@global/colors';
import { isWeb, isMobileWeb, isNative } from '@global/screens';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import Modal from '@components/modal/Modal';
import { NoTranslate } from '@components/shared/TranslationContext';

const dimension = Dimensions.get('window');

const ModalTopRounded = (props) => {
  const insets = useSafeAreaInsets();
  const modalRoundedHeight = isMobileWeb
    ? dimension.height - 100
    : dimension.height - insets.top - insets.bottom - 55;

  const [totalHeight, setTotalHeight] = useState(0);
  const [translateY, setTranslateY] = useState(new Animated.Value(0));

  const onClose = () => {
    try {
      props.onClose();
    } catch (error) {}
  };

  const onPanGestureEvent = (e) => {
    if (!isWeb) {
      const scroll = e?.nativeEvent?.translationY;
      if (scroll >= 0) {
        setTranslateY(scroll);
      }
    }
  };

  const onHandleGestureEvent = (e) => {
    if (!isWeb) {
      const scroll = e?.nativeEvent?.translationY;
      if (scroll >= 0) {
        if (scroll > (totalHeight ? totalHeight / 1.3 : 100)) {
          setTranslateY(new Animated.Value(0));
          onClose();
        } else {
          setTranslateY(new Animated.Value(0));
        }
      }
    }
  };

  // Modal renders its children via a Portal that re-parents them onto a
  // detached PortalHost elsewhere in the tree, so a Context provider
  // wrapping <Modal> itself never reaches the portaled content. It has to
  // wrap the JSX passed *into* Modal instead.
  const innerContent = (
    <PanGestureHandler
      onGestureEvent={onPanGestureEvent}
      onHandlerStateChange={onHandleGestureEvent}>
      <Animated.View
        style={[
          styles.modalTopRounded,
          { maxHeight: modalRoundedHeight },
          { borderRadius: 5 },
          isNative || isMobileWeb
            ? {
                width: '95%',
                marginHorizontal: 10,
                // Default keeps existing screens' behavior (sheet floats a third up the
                // screen). stickToBottom opts a caller into a real bottom sheet flush with
                // the screen edge instead, without touching the other 12+ existing callers.
                bottom: props.stickToBottom
                  ? 0
                  : Dimensions.get('window').height / 3,
                ...(props.stickToBottom
                  ? {
                      width: '100%',
                      marginHorizontal: 0,
                      borderTopLeftRadius: 24,
                      borderTopRightRadius: 24,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }
                  : {}),
              }
            : {},
          { transform: [{ translateY: translateY }] },
        ]}
        onLayout={(e) => {
          setTotalHeight(e?.nativeEvent?.layout?.height);
        }}>
        <View style={styles.modalHeader}>
          <SociollaText
            type="medium"
            style={{ fontSize: 17, letterSpacing: 0.5, width: 260 }}
            ellipsizeMode="tail"
            numberOfLines={1}>
            {props.title}
          </SociollaText>
          <TouchableOpacity onPress={() => onClose()}>
            <Image
              width={16}
              height={16}
              style={{ width: 16, height: 16 }}
              svg={true}
              source={require('@assets/img/icons/close-grey.svg')}
            />
          </TouchableOpacity>
        </View>
        <ScrollView style={{ width: '100%' }}>{props.children}</ScrollView>
      </Animated.View>
    </PanGestureHandler>
  );

  return (
    <Modal
      transparent={true}
      visible={props.visible}
      animationType={'fade'}
      maxWidth={props.maxWidth ? props.maxWidth : '100%'}
      onClose={() => onClose()}>
      {props.noTranslate ? (
        <NoTranslate>{innerContent}</NoTranslate>
      ) : (
        innerContent
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalTopRounded: {
    width: '100%',
    backgroundColor: Colors.white,
    paddingBottom: isWeb && !isMobileWeb ? 0 : 50,
    position: isWeb ? 'relative' : 'absolute',
    bottom: 0,
  },
  modalHeader: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: isWeb ? 20 : 30,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
});

export default React.memo(ModalTopRounded, (prev, next) => prev == next);
